import type { NewAttachment } from '../api.ts';
import {
  FIRST_ATTEMPT,
  decide,
  scaledSize,
  sourceProblem,
  stripDataUrl,
  type Attempt,
} from './image.ts';

/**
 * Turn a file somebody chose into an attachment the API will accept.
 *
 * The image is always decoded and redrawn, never sent as picked. Redrawing onto
 * a canvas and re-encoding keeps only pixels: EXIF — camera, timestamps and GPS
 * position included — does not survive it, which matters because a screenshot
 * taken on a phone can carry where the person was standing when they took it.
 * It is also what makes the size limit reachable at all.
 *
 * Every decision (how small, which quality next, whether it fits) is in
 * `image.ts`, where it is tested. This file is the part only a browser can run.
 */

export class ImageProblem extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImageProblem';
  }
}

export interface PreparedImage {
  readonly attachment: NewAttachment;
  /** The encoded image, kept for its size. */
  readonly blob: Blob;
}

interface Decoded {
  readonly source: CanvasImageSource;
  readonly width: number;
  readonly height: number;
  readonly release: () => void;
}

const readDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('The image could not be read.'));
    reader.readAsDataURL(blob);
  });

const decode = async (file: File): Promise<Decoded> => {
  if (typeof createImageBitmap === 'function') {
    // `from-image` applies the EXIF orientation before it is thrown away, so a
    // portrait photo does not arrive on its side.
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close() };
  }
  // Older browsers without createImageBitmap: decode through an <img> from a
  // data URL, for the same CSP reason the preview uses one.
  const image = new Image();
  image.src = await readDataUrl(file);
  await image.decode();
  return { source: image, width: image.naturalWidth, height: image.naturalHeight, release: () => undefined };
};

const toBlob = (canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

const readBase64 = async (blob: Blob): Promise<string> => stripDataUrl(await readDataUrl(blob));

const draw = (canvas: HTMLCanvasElement, decoded: Decoded, opaque: boolean): void => {
  const context = canvas.getContext('2d');
  if (context === null) throw new ImageProblem('This browser cannot prepare images.');
  context.clearRect(0, 0, canvas.width, canvas.height);
  if (opaque) {
    // JPEG has no transparency, and a transparent pixel encodes as black. A
    // screenshot with a transparent margin should come out on paper-white, as
    // it would look pasted into a document; this is image data, not styling.
    context.fillStyle = 'white';
    context.fillRect(0, 0, canvas.width, canvas.height);
  }
  context.drawImage(decoded.source, 0, 0, canvas.width, canvas.height);
};

export const prepareImage = async (file: File): Promise<PreparedImage> => {
  const problem = sourceProblem(file);
  if (problem !== null) throw new ImageProblem(problem);

  let decoded: Decoded;
  try {
    decoded = await decode(file);
  } catch {
    throw new ImageProblem('That file could not be opened as an image.');
  }

  try {
    const size = scaledSize(decoded.width, decoded.height);
    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;
    draw(canvas, decoded, false);

    let attempt: Attempt = FIRST_ATTEMPT;
    for (;;) {
      const blob = await toBlob(canvas, attempt.format, attempt.quality);
      const decision = decide(attempt, blob);
      if (decision.kind === 'fail') throw new ImageProblem(decision.reason);
      if (decision.kind === 'done') {
        const data = await readBase64(decision.blob);
        return { attachment: { mediaType: decision.mediaType, data }, blob: decision.blob };
      }
      if (decision.redraw) draw(canvas, decoded, true);
      attempt = decision.attempt;
    }
  } finally {
    decoded.release();
  }
};
